import { Injectable, ErrorHandler, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';

import { ToastService } from '../toast/toast.service';

/**
 * Error severity levels for categorization
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Error types for categorization
 */
export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  UNKNOWN = 'unknown',
  FIREBASE = 'firebase',
  JAVASCRIPT = 'javascript'
}

/**
 * Structured error interface for consistent error handling
 */
export interface AppError {
  id: string;
  message: string;
  originalError: Error;
  type: ErrorType;
  severity: ErrorSeverity;
  timestamp: Date;
  userAgent?: string;
  url?: string;
  userId?: string;
  context?: Record<string, unknown>;
}

/**
 * Configuration for error handling behavior
 */
export interface ErrorHandlerConfig {
  enableConsoleLogging: boolean;
  enableRemoteLogging: boolean;
  showUserNotifications: boolean;
  maxRetryAttempts: number;
}

/**
 * Global error handler service that provides centralized error management,
 * categorization, and logging capabilities for the entire application.
 * 
 * Features:
 * - Error categorization by type and severity
 * - Structured error logging with context
 * - Integration with toast notifications
 * - Firebase error handling
 * - HTTP error response handling
 * - Configurable behavior
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
  
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  
  private config: ErrorHandlerConfig = {
    enableConsoleLogging: true,
    enableRemoteLogging: false,
    showUserNotifications: true,
    maxRetryAttempts: 3
  };

  /**
   * Handles any unhandled errors in the application
   * @param error - The error to handle
   */
  handleError(error: unknown): void {
    const appError = this.createAppError(error);
    this.processError(appError);
  }

  /**
   * Handles HTTP errors specifically
   * @param error - HTTP error response
   * @returns Processed app error
   */
  handleHttpError(error: HttpErrorResponse): AppError {
    const appError = this.createAppError(error);
    appError.type = ErrorType.NETWORK;
    
    // Categorize based on status code
    if (error.status >= 400 && error.status < 500) {
      appError.severity = error.status === 401 || error.status === 403 
        ? ErrorSeverity.HIGH 
        : ErrorSeverity.MEDIUM;
      
      if (error.status === 401 || error.status === 403) {
        appError.type = ErrorType.AUTHENTICATION;
      } else if (error.status === 400 || error.status === 422) {
        appError.type = ErrorType.VALIDATION;
      }
    } else if (error.status >= 500) {
      appError.severity = ErrorSeverity.CRITICAL;
    }

    this.processError(appError);
    return appError;
  }

  /**
   * Handles Firebase-specific errors
   * @param error - Firebase error
   * @param context - Additional context information
   * @returns Processed app error
   */
  handleFirebaseError(error: Error, context?: Record<string, unknown>): AppError {
    const appError = this.createAppError(error);
    appError.type = ErrorType.FIREBASE;
    appError.context = { ...appError.context, ...context };

    // Categorize Firebase errors
    const errorCode = (error as any).code;
    if (errorCode) {
      if (errorCode.includes('auth/')) {
        appError.type = ErrorType.AUTHENTICATION;
        appError.severity = ErrorSeverity.HIGH;
      } else if (errorCode.includes('permission-denied')) {
        appError.type = ErrorType.PERMISSION;
        appError.severity = ErrorSeverity.HIGH;
      } else if (errorCode.includes('network')) {
        appError.type = ErrorType.NETWORK;
        appError.severity = ErrorSeverity.MEDIUM;
      }
    }

    this.processError(appError);
    return appError;
  }

  /**
   * Handles validation errors
   * @param message - Validation error message
   * @param context - Additional context information
   * @returns Processed app error
   */
  handleValidationError(message: string, context?: Record<string, unknown>): AppError {
    const error = new Error(message);
    const appError = this.createAppError(error);
    appError.type = ErrorType.VALIDATION;
    appError.severity = ErrorSeverity.LOW;
    appError.context = { ...appError.context, ...context };

    this.processError(appError);
    return appError;
  }

  /**
   * Updates the error handler configuration
   * @param config - Partial configuration to update
   */
  updateConfig(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Gets the current configuration
   * @returns Current error handler configuration
   */
  getConfig(): ErrorHandlerConfig {
    return { ...this.config };
  }

  /**
   * Creates a structured AppError from any error type
   * @param error - The original error
   * @returns Structured AppError
   */
  private createAppError(error: unknown): AppError {
    const timestamp = new Date();
    const id = this.generateErrorId(timestamp);
    
    let message: string;
    let originalError: Error;
    let type = ErrorType.UNKNOWN;
    let severity = ErrorSeverity.MEDIUM;

    if (error instanceof HttpErrorResponse) {
      message = `HTTP ${error.status}: ${error.message}`;
      originalError = new Error(message);
      type = ErrorType.NETWORK;
      severity = error.status >= 500 ? ErrorSeverity.CRITICAL : ErrorSeverity.MEDIUM;
    } else if (error instanceof Error) {
      message = error.message || 'Unknown error occurred';
      originalError = error;
      
      // Check for Firebase errors
      if ((error as any).code) {
        type = ErrorType.FIREBASE;
      } else if (error.name === 'TypeError' || error.name === 'ReferenceError') {
        type = ErrorType.JAVASCRIPT;
        severity = ErrorSeverity.HIGH;
      }
    } else if (typeof error === 'string') {
      message = error;
      originalError = new Error(error);
    } else {
      message = 'An unexpected error occurred';
      originalError = new Error(JSON.stringify(error));
    }

    return {
      id,
      message,
      originalError,
      type,
      severity,
      timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href,
      context: {
        stack: originalError.stack,
        errorType: originalError.constructor.name
      }
    };
  }

  /**
   * Processes the error by logging and notifying
   * @param appError - The structured error to process
   */
  private processError(appError: AppError): void {
    // Log to console if enabled
    if (this.config.enableConsoleLogging) {
      this.logToConsole(appError);
    }

    // Log remotely if enabled (Firebase, Sentry, etc.)
    if (this.config.enableRemoteLogging) {
      this.logRemotely(appError);
    }

    // Show user notification if appropriate (async, but don't block)
    if (this.config.showUserNotifications && this.shouldShowUserNotification(appError)) {
      this.notifyUser(appError).catch(error => {
        console.error('Failed to notify user of error:', error);
      });
    }
  }

  /**
   * Logs error to console with appropriate level
   * @param appError - The error to log
   */
  private logToConsole(appError: AppError): void {
    const logData = {
      id: appError.id,
      message: appError.message,
      type: appError.type,
      severity: appError.severity,
      timestamp: appError.timestamp.toISOString(),
      url: appError.url,
      context: appError.context
    };

    switch (appError.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        console.error('🚨 App Error:', logData, appError.originalError);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('⚠️ App Warning:', logData, appError.originalError);
        break;
      case ErrorSeverity.LOW:
        console.info('ℹ️ App Info:', logData);
        break;
    }
  }

  /**
   * Logs error to remote service (to be implemented based on requirements)
   * @param appError - The error to log remotely
   */
  private logRemotely(appError: AppError): void {
    // TODO: Implement remote logging (Firebase, Sentry, custom endpoint)
    // This could send errors to Firebase Functions, Sentry, or another service
    console.info('📡 Remote logging would send:', {
      id: appError.id,
      message: appError.message,
      type: appError.type,
      severity: appError.severity,
      timestamp: appError.timestamp.toISOString()
    });
  }

  /**
   * Determines if user should be notified about this error
   * @param appError - The error to evaluate
   * @returns Whether to show user notification
   */
  private shouldShowUserNotification(appError: AppError): boolean {
    // Don't show notifications for low severity errors
    if (appError.severity === ErrorSeverity.LOW) {
      return false;
    }

    // Don't show notifications for certain error types
    if (appError.type === ErrorType.JAVASCRIPT && appError.severity === ErrorSeverity.MEDIUM) {
      return false;
    }

    return true;
  }

  /**
   * Notifies user about the error using toast notifications
   * @param appError - The error to notify about
   */
  private async notifyUser(appError: AppError): Promise<void> {
    const userMessage = this.getUserFriendlyMessage(appError);
    
    try {
      switch (appError.severity) {
        case ErrorSeverity.CRITICAL:
        case ErrorSeverity.HIGH:
          await this.toastService.showError(userMessage);
          break;
        case ErrorSeverity.MEDIUM:
          if (appError.type === ErrorType.NETWORK) {
            await this.toastService.showWarning(userMessage);
          } else {
            await this.toastService.showInfo(userMessage);
          }
          break;
        case ErrorSeverity.LOW:
          await this.toastService.showInfo(userMessage);
          break;
      }
    } catch (toastError) {
      // If toast notification fails, log it but don't create infinite loop
      console.error('Failed to show toast notification:', toastError);
    }
  }

  /**
   * Converts technical error messages to user-friendly messages
   * @param appError - The error to convert
   * @returns User-friendly error message
   */
  private getUserFriendlyMessage(appError: AppError): string {
    switch (appError.type) {
      case ErrorType.NETWORK:
        if (appError.severity === ErrorSeverity.CRITICAL) {
          return this.translocoService.translate('errors.network.critical');
        }
        return this.translocoService.translate('errors.network.default');
      
      case ErrorType.AUTHENTICATION:
        return this.translocoService.translate('errors.authentication');
      
      case ErrorType.PERMISSION:
        return this.translocoService.translate('errors.permission');
      
      case ErrorType.VALIDATION:
        return appError.message; // Validation messages are usually user-friendly
      
      case ErrorType.FIREBASE:
        return this.translocoService.translate('errors.firebase');
      
      case ErrorType.JAVASCRIPT:
        if (appError.severity === ErrorSeverity.HIGH) {
          return this.translocoService.translate('errors.javascript.high');
        }
        return this.translocoService.translate('errors.javascript.default');
      
      default:
        return this.translocoService.translate('errors.default');
    }
  }

  /**
   * Generates a unique error ID
   * @param timestamp - The error timestamp
   * @returns Unique error ID
   */
  private generateErrorId(timestamp: Date): string {
    const timeString = timestamp.getTime().toString();
    const randomString = Math.random().toString(36).substring(2, 8);
    return `err_${timeString}_${randomString}`;
  }
}